import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../config/axiosConfig';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const fmtNumber = (value, decimals = 0) =>
    Number(value || 0).toLocaleString('es-CL', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

const fmtDate = (value) => {
    if (!value) return '-';
    const [date] = String(value).split(' ');
    const [y, m, d] = date.split('-');
    return y && m && d ? `${d}-${m}-${y}` : value;
};

const fmtDayDate = (value) => {
    if (!value) return '-';
    const [date] = String(value).split(' ');
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return value;

    const dayName = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'][
        new Date(y, m - 1, d).getDay()
    ];

    return `${dayName} ${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}`;
};

const toMonthValue = (date = new Date()) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const addMonths = (year, monthIndex, offset) => new Date(year, monthIndex + offset, 1);

const buildPeriodRange = (periodoDesde, periodoHasta = periodoDesde) => {
    const [yearDesde, monthDesde] = String(periodoDesde || '').split('-').map(Number);
    const [yearHasta, monthHasta] = String(periodoHasta || periodoDesde || '').split('-').map(Number);

    if (!yearDesde || !monthDesde || !yearHasta || !monthHasta) {
        return { desde: '', hasta: '' };
    }

    const startMonth = addMonths(yearDesde, monthDesde - 1, -1);
    const desde = `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}-26`;
    const hasta = `${yearHasta}-${String(monthHasta).padStart(2, '0')}-25`;

    return { desde, hasta };
};

const KPI = ({ icon, label, value, hint, tone = 'primary' }) => (
    <div className="col">
        <div className="card border-0 shadow-sm h-100">
            <div className="card-body p-3">
                <div className="d-flex align-items-start justify-content-between gap-2">
                    <div>
                        <div className="small text-muted fw-semibold mb-1">{label}</div>
                        <div className="fs-5 fw-bold text-dark lh-sm">{value}</div>
                        {hint && <div className="text-muted mt-1" style={{ fontSize: '0.76rem', lineHeight: 1.2 }}>{hint}</div>}
                    </div>
                    <span className={`badge bg-${tone}-subtle text-${tone} border border-${tone}-subtle rounded-2 p-2 flex-shrink-0`}>
                        <i className={`bi ${icon}`} style={{ fontSize: '1rem' }} />
                    </span>
                </div>
            </div>
        </div>
    </div>
);

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { position: 'bottom' },
        tooltip: { mode: 'index', intersect: false },
    },
    scales: {
        y: { beginAtZero: true },
    },
};

const buildDiarioOptions = (diario) => ({
    ...chartOptions,
    plugins: {
        ...chartOptions.plugins,
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label(ctx) {
                    const label = ctx.dataset.label || '';
                    const val = ctx.parsed.y;
                    if (label === 'Horas calculadas') {
                        const personas = Number(diario[ctx.dataIndex]?.usuarios_activos || 0);
                        return `${label}: ${val} h  (${personas} persona${personas !== 1 ? 's' : ''})`;
                    }
                    return `${label}: ${val}`;
                },
            },
        },
    },
    scales: {
        y: { beginAtZero: true, position: 'left', title: { display: true, text: 'Horas / Usuarios' } },
    },
});

const dailyThresholdPlugin = {
    id: 'dailyThresholdPlugin',
    afterDatasetsDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        const yScale = scales.y;

        if (!yScale || !chartArea) return;

        [
            { value: 3, label: '3h', color: '#fd7e14' },
            { value: 5, label: '5h', color: '#dc3545' },
        ].forEach((threshold) => {
            const y = yScale.getPixelForValue(threshold.value);
            if (y < chartArea.top || y > chartArea.bottom) return;

            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([6, 5]);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = threshold.color;
            ctx.moveTo(chartArea.left, y);
            ctx.lineTo(chartArea.right, y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = threshold.color;
            ctx.font = '600 11px Arial';
            ctx.textAlign = 'right';
            ctx.fillText(threshold.label, chartArea.right - 4, y - 6);
            ctx.restore();
        });
    },
};

function UsoCalendario() {
    const minutosInactividad = 40;
    const currentMonth = toMonthValue();
    const [periodoDesde, setPeriodoDesde] = useState(currentMonth);
    const [periodoHasta, setPeriodoHasta] = useState(currentMonth);
    const [usarRangoMeses, setUsarRangoMeses] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const cargar = async () => {
        const periodoHastaConsulta = usarRangoMeses ? periodoHasta : periodoDesde;
        const { desde, hasta } = buildPeriodRange(periodoDesde, periodoHastaConsulta);

        if (!desde || !hasta) {
            setError('Debes seleccionar un periodo valido.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const resp = await axiosInstance.get('/auditoria-turnos/uso-calendario', {
                params: {
                    minutos_inactividad: minutosInactividad,
                    desde,
                    hasta,
                },
            });

            if (resp.data?.error) {
                setError(resp.data.message || 'No se pudo cargar el reporte.');
                setData(null);
                return;
            }

            setData(resp.data?.data || null);
        } catch (err) {
            console.error(err);
            setError('Error al conectar con el servidor.');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const usuarios = useMemo(() => data?.usuarios || [], [data]);
    const usuariosGlobal = useMemo(() => data?.usuarios_global || [], [data]);
    const diario = useMemo(() => data?.diario || [], [data]);
    const global = data?.global || {};
    const parametros = data?.parametros || {};
    const totalesUsuarios = useMemo(() => {
        return usuarios.reduce((acc, usuario) => {
            acc.diasSobre3 += Number(usuario.cantidad_dias_mas_de_3_horas || 0);
            acc.diasSobre5 += Number(usuario.cantidad_dias_mas_de_5_horas || 0);
            return acc;
        }, {
            diasSobre3: 0,
            diasSobre5: 0,
        });
    }, [usuarios]);

    const topPromedio = useMemo(
        () => [...usuarios].sort((a, b) => Number(b.promedio_horas_por_dia_trabajado || 0) - Number(a.promedio_horas_por_dia_trabajado || 0)).slice(0, 10),
        [usuarios]
    );

    const topInteracciones = useMemo(
        () => [...usuarios].sort((a, b) => Number(b.total_interacciones || 0) - Number(a.total_interacciones || 0)).slice(0, 10),
        [usuarios]
    );

    const diarioOptions = useMemo(() => buildDiarioOptions(diario), [diario]);
    const periodoHastaConsulta = usarRangoMeses ? periodoHasta : periodoDesde;
    const rangoPeriodo = useMemo(
        () => buildPeriodRange(periodoDesde, periodoHastaConsulta),
        [periodoDesde, periodoHastaConsulta]
    );

    const chartDiario = {
        labels: diario.map((d) => fmtDayDate(d.dia)),
        datasets: [
            {
                label: 'Horas calculadas',
                data: diario.map((d) => Number(d.total_horas || 0)),
                borderColor: '#0d6efd',
                backgroundColor: 'rgba(13, 110, 253, 0.16)',
                tension: 0.25,
            },
            {
                label: 'Usuarios sobre 3h',
                data: diario.map((d) => Number(d.usuarios_sobre_3_horas || 0)),
                borderColor: '#fd7e14',
                backgroundColor: 'rgba(253, 126, 20, 0.16)',
                tension: 0.25,
            },
            {
                label: 'Usuarios sobre 5h',
                data: diario.map((d) => Number(d.usuarios_sobre_5_horas || 0)),
                borderColor: '#dc3545',
                backgroundColor: 'rgba(220, 53, 69, 0.16)',
                tension: 0.25,
            },
        ],
    };

    const chartTopPromedio = {
        labels: topPromedio.map((u) => u.usuario_nombre),
        datasets: [
            {
                label: 'Promedio horas por dia trabajado',
                data: topPromedio.map((u) => Number(u.promedio_horas_por_dia_trabajado || 0)),
                backgroundColor: 'rgba(13, 110, 253, 0.72)',
            },
        ],
    };

    const chartTopInteracciones = {
        labels: topInteracciones.map((u) => u.usuario_nombre),
        datasets: [
            {
                label: 'Interacciones',
                data: topInteracciones.map((u) => Number(u.total_interacciones || 0)),
                backgroundColor: 'rgba(25, 135, 84, 0.72)',
            },
        ],
    };

    return (
        <div className="container-fluid py-4 px-4">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-clock-history fs-3 text-primary" />
                    <div>
                        <h4 className="mb-0 fw-bold">Tiempo de Uso e Interaccion con Calendario</h4>
                        <small className="text-muted">
                            Desde {fmtDate(rangoPeriodo.desde)} hasta {fmtDate(rangoPeriodo.hasta)}.
                        </small>
                    </div>
                </div>

                <div className="d-flex flex-wrap align-items-end gap-2">
                    <div style={{ width: 170 }}>
                        <label className="form-label small fw-semibold mb-1">Mes periodo</label>
                        <input
                            type="month"
                            className="form-control"
                            value={periodoDesde}
                            onChange={(e) => {
                                const nextPeriodo = e.target.value;
                                setPeriodoDesde(nextPeriodo);
                                if (!usarRangoMeses || periodoHasta < nextPeriodo) {
                                    setPeriodoHasta(nextPeriodo);
                                }
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && cargar()}
                        />
                    </div>
                    {usarRangoMeses && (
                        <div style={{ width: 170 }}>
                            <label className="form-label small fw-semibold mb-1">Hasta mes</label>
                            <input
                                type="month"
                                className="form-control"
                                value={periodoHasta}
                                min={periodoDesde}
                                onChange={(e) => setPeriodoHasta(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && cargar()}
                            />
                        </div>
                    )}
                    <div className="form-check mb-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="usoCalendarioRangoMeses"
                            checked={usarRangoMeses}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setUsarRangoMeses(checked);
                                if (!checked) {
                                    setPeriodoHasta(periodoDesde);
                                }
                            }}
                        />
                        <label className="form-check-label small" htmlFor="usoCalendarioRangoMeses">
                            Rango de meses
                        </label>
                    </div>
                    <button className="btn btn-primary" onClick={cargar} disabled={loading}>
                        {loading ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-arrow-clockwise me-2" />}
                        Actualizar
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {error}
                </div>
            )}

            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 mb-4">
                <KPI icon="bi-hourglass-split" label="Horas calendario" value={fmtNumber(global.total_horas, 2)} hint="Tiempo efectivo por bloques" />
                <KPI icon="bi-people" label="Usuarios activos" value={fmtNumber(global.usuarios_activos)} hint={`${fmtNumber(global.dias_con_actividad)} dias con actividad`} tone="success" />
                <KPI icon="bi-calendar2-check" label="Promedio usuario/dia" value={`${fmtNumber(global.promedio_horas_por_usuario_dia, 2)} h`} hint="Solo dias trabajados" tone="info" />
                <KPI icon="bi-clock" label="Dias usuario sobre 3h" value={fmtNumber(totalesUsuarios.diasSobre3)} hint="Umbral base de actividad" tone="warning" />
                <KPI icon="bi-exclamation-octagon" label="Dias usuario sobre 5h" value={fmtNumber(totalesUsuarios.diasSobre5)} hint="Usuarios con jornadas largas" tone="danger" />
                <KPI icon="bi-cursor" label="Interacciones" value={fmtNumber(global.total_interacciones)} hint={`${fmtNumber(global.interacciones_por_hora, 2)} por hora`} tone="secondary" />
                <KPI icon="bi-collection" label="Bloques de uso" value={fmtNumber(global.total_bloques)} hint={`${fmtDate(rangoPeriodo.desde)} a ${fmtDate(rangoPeriodo.hasta)}`} tone="warning" />
                <KPI icon="bi-database-check" label="Registros auditoria" value={fmtNumber(parametros.total_registros)} hint="Base total disponible" tone="secondary" />
                <KPI
                    icon="bi-person-up"
                    label="Mayor promedio"
                    value={global.usuario_mayor_promedio?.usuario_nombre || '-'}
                    hint={global.usuario_mayor_promedio ? `${fmtNumber(global.usuario_mayor_promedio.promedio_horas_por_dia_trabajado, 2)} h/dia` : ''}
                    tone="primary"
                />
                <KPI
                    icon="bi-trophy"
                    label="Mayor total"
                    value={global.usuario_mayor_total?.usuario_nombre || '-'}
                    hint={global.usuario_mayor_total ? `${fmtNumber(global.usuario_mayor_total.total_horas, 2)} horas` : ''}
                    tone="dark"
                />
            </div>

            <div className="row g-3 mb-4">
                <div className="col-12 col-xl-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white fw-semibold">Uso diario global</div>
                        <div className="card-body" style={{ height: 330 }}>
                            <Line data={chartDiario} options={diarioOptions} plugins={[dailyThresholdPlugin]} />
                        </div>
                    </div>
                </div>
                <div className="col-12 col-xl-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white fw-semibold">Top promedio diario por usuario</div>
                        <div className="card-body" style={{ height: 330 }}>
                            <Bar data={chartTopPromedio} options={chartOptions} />
                        </div>
                    </div>
                </div>
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white fw-semibold">Top interacciones</div>
                        <div className="card-body" style={{ height: 330 }}>
                            <Bar data={chartTopInteracciones} options={chartOptions} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Detalle por usuario</span>
                    <span className="badge bg-primary rounded-pill">{fmtNumber(usuarios.length)} usuarios</span>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover table-sm align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-3">Usuario</th>
                                    <th>Dias</th>
                                    <th>Prom. h/dia</th>
                                    <th>Total h</th>
                                    <th>Interacciones</th>
                                    <th>Int/h</th>
                                    <th>Bloques</th>
                                    <th>Dias &gt; 3h</th>
                                    <th>Dias &gt; 5h</th>
                                    <th>Dia mas alto</th>
                                    <th>Horas dia mas alto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuarios.length === 0 && (
                                    <tr>
                                        <td colSpan="11" className="text-center text-muted py-4">Sin datos de auditoria.</td>
                                    </tr>
                                )}
                                {usuarios.map((u) => (
                                    <tr key={u.usuario_id}>
                                        <td className="ps-3 fw-semibold">{u.usuario_nombre}</td>
                                        <td>{fmtNumber(u.dias_trabajados)}</td>
                                        <td>{fmtNumber(u.promedio_horas_por_dia_trabajado, 2)}</td>
                                        <td>{fmtNumber(u.total_horas, 2)}</td>
                                        <td>{fmtNumber(u.total_interacciones)}</td>
                                        <td>{fmtNumber(u.interacciones_por_hora, 2)}</td>
                                        <td>{fmtNumber(u.total_bloques)}</td>
                                        <td>
                                            <span className={`badge ${Number(u.cantidad_dias_mas_de_3_horas || 0) > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                {fmtNumber(u.cantidad_dias_mas_de_3_horas)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${Number(u.cantidad_dias_mas_de_5_horas || 0) > 0 ? 'bg-danger' : 'bg-secondary'}`}>
                                                {fmtNumber(u.cantidad_dias_mas_de_5_horas)}
                                            </span>
                                        </td>
                                        <td>{fmtDayDate(u.dia_con_mas_horas)}</td>
                                        <td>{fmtNumber(u.horas_dia_con_mas_horas, 2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Detalle por usuario global</span>
                    <span className="badge bg-dark rounded-pill">{fmtNumber(usuariosGlobal.length)} usuarios</span>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover table-sm align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-3">Usuario / Coordinador</th>
                                    <th>Registro eventos</th>
                                    <th>Dias eventos</th>
                                    <th>Prom. eventos/dia</th>
                                    <th>Primer evento</th>
                                    <th>Ultima actividad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuariosGlobal.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted py-4">Sin datos de registro de eventos.</td>
                                    </tr>
                                )}
                                {usuariosGlobal.map((u) => (
                                    <tr key={`global_${u.usuario_id}`}>
                                        <td className="ps-3 fw-semibold">{u.usuario_nombre}</td>
                                        <td>
                                            <span className="badge bg-success-subtle text-success border border-success-subtle">
                                                {fmtNumber(u.total_registro_eventos)}
                                            </span>
                                        </td>
                                        <td>{fmtNumber(u.dias_registro_eventos)}</td>
                                        <td>{fmtNumber(u.promedio_eventos_por_dia, 2)}</td>
                                        <td>{fmtDayDate(u.primer_registro_eventos)}</td>
                                        <td>{fmtDayDate(u.ultimo_registro_eventos)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UsoCalendario;
